import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { Observable, Subscription, combineLatest } from 'rxjs';

import { AppState } from 'src/app/state';
import {
  selectIsLoadingTemplates,
  selectTemplatesLoaded,
  selectTemplates,
  selectSelectedTemplate,
} from 'src/app/dashboard/state/templates/templates.selectors';
import { openDashboardConfirmationDialog } from 'src/app/dashboard/state/dashboard.actions';
import {
  deleteTemplate,
  exportTemplate,
  getTemplate,
  setSelectedTemplate,
} from 'src/app/dashboard/state/templates/templates.actions';
import { Study } from 'src/app/models/study';
import { Template } from 'src/app/models/template';
import { Intervention } from 'src/app/models/intervention';
import { environment } from 'src/environments/environment';
import { selectInterventions } from 'src/app/dashboard/state/intervention/intervention.selectors';
import { getSections } from 'src/app/dashboard/state/sections/sections.actions';
import { ImportTemplateComponent } from '../../components/import-template/import-template.component';

@Component({
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss'],
})
export class TemplatesComponent implements OnInit, OnDestroy {
  loadingTemplates$: Observable<boolean>;
  templatesLoaded$: Observable<boolean>;

  selectedStudy: Study;
  templates: Template[] = [];
  selectedTemplate: Template;

  ccFeedbackUrl = environment.ccFeedbackUrl;

  interventionNameLengthLimit = 30;
  availableInterventions: Intervention[] = [];

  isEdit = false;

  private subscriptions = new Subscription();

  constructor(private store: Store<AppState>, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.loadingTemplates$ = this.store.select(selectIsLoadingTemplates);
    this.templatesLoaded$ = this.store.select(selectTemplatesLoaded);
    this.subscriptions.add(
      combineLatest([this.store.select(selectInterventions), this.store.select(selectTemplates)]).subscribe(
        ([interventions, templates]) => {
          this.availableInterventions = interventions.slice();
          const transformedTemplates = [];
          templates?.forEach((template) => {
            const templateIntervention = interventions?.find(
              (intervention) => intervention.id === template.interventionId
            );
            if (templateIntervention) {
              this.availableInterventions.splice(this.availableInterventions.indexOf(templateIntervention), 1);
              template = {
                ...template,
                interventionName: templateIntervention?.name,
              };
            }
            if (!transformedTemplates.find((temp) => template.id === temp.id)) {
              transformedTemplates.push(template);
            }
          });
          this.templates = transformedTemplates;
        }
      )
    );
    this.subscriptions.add(
      this.store.select(selectSelectedTemplate).subscribe((template) => {
        if (template) {
          this.isEdit = true;
          this.selectedTemplate = template;
        } else {
          this.isEdit = false;
        }
      })
    );
  }

  createTemplate(): void {
    this.isEdit = false;
    this.store.dispatch(setSelectedTemplate({ selectedTemplate: null }));
  }

  openDeleteTemplateDialog(template: Template) {
    this.store.dispatch(
      openDashboardConfirmationDialog({
        dialogData: {
          title: 'Are you sure you want to delete this template?',
          onConfirm: deleteTemplate({ template }),
        },
      })
    );
  }

  openTemplateDetails(template: Template) {
    this.isEdit = true;
    this.store.dispatch(getTemplate({ templateId: template.id }));
    this.store.dispatch(getSections({ templateId: template.id }));
  }

  openImportTemplate() {
    this.dialog.open(ImportTemplateComponent);
  }

  exportTemplate(template: Template) {
    this.store.dispatch(exportTemplate({ template }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
